import React, { FC, useState, useCallback, useRef } from 'react';
import { ErrorMessage, useField, useFormikContext } from 'formik';
import { useRifm } from 'rifm';
import formatPriceNumber from 'utils/formatPrice';
import round from 'lodash/round';
import {
  Container,
  Component,
  ComponentTextArea,
  Label,
  IconError,
  InputContainer,
  Error,
  IconVisability,
  ImageContainer,
  LoaderOverlay,
  ComponentFile,
  ImagePreview,
  AddImage,
  DragNDropText,
  FormatHint,
  LoaderText,
  PlaceholderContainer,
  Spinner,
} from './styles';
import { Props, ImageInputProps } from './types';

// TODO refactor this
// We should have a base input disconnected from formik
// And create other components attached to formik or input number
const Input: FC<Props> = ({
  className,
  label,
  value,
  textArea,
  disabled,
  ...props
}) => {
  const [field, meta] = useField(props);
  const hasError = !!(meta.touched && meta.error);

  return (
    <Container className={className}>
      {label && (
        <Label as="label" $disabled={disabled} htmlFor={field.name}>
          {label}
        </Label>
      )}

      {textArea ? (
        <InputContainer>
          <ComponentTextArea
            {...field}
            {...props}
            id={field.name}
            disabled={disabled}
          >
            {value}
          </ComponentTextArea>
        </InputContainer>
      ) : (
        <InputContainer>
          <Component
            {...field}
            {...props}
            id={field.name}
            disabled={disabled}
          />
          {hasError && <IconError />}
          {props.visabilityClickHandler && (
            <IconVisability onClick={props.visabilityClickHandler} />
          )}
        </InputContainer>
      )}
      {/* @ts-ignore */}
      <ErrorMessage component={Error} name={field.name} />
    </Container>
  );
};

export default Input;

const numbersAccept = /\d+/g;
const onlyNumbers = (string: string) =>
  (string.match(numbersAccept) || []).join('');
const normalizeHead = (head: string) => {
  const parsed = onlyNumbers(head);
  const number = parseInt(parsed, 10);

  if (Number.isNaN(number)) {
    return '0';
  }

  return number.toLocaleString('en');
};

const normalizeTail = (tail: string) => {
  if (!tail) {
    return '';
  }
  return tail;
};

const numberWithDotAccept = /[\d.]+/g;
const onlyNumberAndDot = (string: string) =>
  (string.match(numberWithDotAccept) || [0]).join('');

const handleFormat = (valueToFormat: string) => {
  if (valueToFormat === '$' || valueToFormat === '') {
    return '$';
  }
  if (!valueToFormat.includes('.')) {
    return `$${normalizeHead(valueToFormat)}`;
  }

  const parsed = onlyNumberAndDot(valueToFormat);
  const [head, ...tails] = parsed.split('.');
  const tail = tails.join('');
  return `$${normalizeHead(head)}.${normalizeTail(tail)}`;
};

const toPayload = (string: string): number => {
  const withoutDollar = parseFloat(onlyNumberAndDot(string));
  return round(withoutDollar, 2);
};

// TODO: TFR-49 split components into two or more different logical components

export const PriceInput: FC<Props> = ({
  label,
  value,
  className,
  disabled,
  ...props
}) => {
  const [localValue, setLocalValue] = useState(formatPriceNumber(value));
  const [field, meta, { setValue }] = useField(props);
  const hasError = !!(meta.touched && meta.error);

  const handleOnChange = useCallback(
    (newValue: string) => setLocalValue(newValue),
    [setLocalValue],
  );

  const handleBlur = useCallback(() => {
    const payloadValue = toPayload(localValue);
    setLocalValue(formatPriceNumber(payloadValue * 100));
    setValue(payloadValue * 100);
  }, [setLocalValue, setValue, localValue]);

  const rifm = useRifm({
    accept: /[\d.$]/g,
    value: localValue,
    onChange: handleOnChange,
    format: handleFormat,
  });

  return (
    <Container className={className}>
      {label && (
        <Label as="label" $disabled={disabled} htmlFor={field.name}>
          {label}
        </Label>
      )}

      <InputContainer>
        <Component
          {...props}
          id={field.name}
          disabled={disabled}
          value={rifm.value}
          onChange={rifm.onChange}
          onBlur={handleBlur}
        />
        {hasError && <IconError />}
      </InputContainer>
      {/* @ts-ignore */}
      <ErrorMessage component={Error} name={field.name} />
    </Container>
  );
};

export const FileInput: FC<ImageInputProps> = ({
  label,
  className,
  disabled,
  value,
  onFileChange,
  isLoading,
  ...props
}) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [field, meta, { setValue, setTouched, setError }] = useField<
    string | null
  >(props);
  const fileField = useRef<HTMLInputElement>(null);
  const { setFieldValue } = useFormikContext();
  const [isDragActive, setIsDragActive] = useState(false);

  const handleOnClick = useCallback(() => {
    if (fileField.current == null || disabled || isLoading) return;
    fileField.current.click();
  }, [fileField, disabled, isLoading]);

  const changeFile = useCallback(
    (files: FileList | null) => {
      if (files == null || files.length !== 1) {
        setError('Only one file allowed');
        setTouched(true, false);
        return;
      }

      if (['image/png', 'image/jpeg'].indexOf(files[0].type) === -1) {
        setError('jpeg or png is allowed');
        setTouched(true, false);
        return;
      }

      if (files[0].size > 9 * 1024 * 1024) {
        setError('File size is too big');
        setTouched(true, false);
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        if (reader.readyState === 2) {
          setValue(reader.result as string);
        }
      };
      reader.readAsDataURL(files[0]);
      onFileChange(files[0], setFieldValue);
    },
    [onFileChange, setError, setFieldValue, setTouched, setValue],
  );

  const handleOnChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      event.preventDefault();
      changeFile(event.target.files);
    },
    [changeFile],
  );

  const handleDragOver = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      if (!isDragActive) setIsDragActive(true);
    },
    [isDragActive, setIsDragActive],
  );

  const handleDragLeave = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      if (isDragActive) setIsDragActive(false);
    },
    [isDragActive, setIsDragActive],
  );

  const handleOnDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      changeFile(event.dataTransfer.files);
      setIsDragActive(false);
    },
    [changeFile],
  );

  return (
    <Container className={className} role="presentation" tabIndex={0}>
      {label && (
        <Label as="label" $disabled={disabled} htmlFor={field.name}>
          {label}
        </Label>
      )}

      <ComponentFile
        id={field.name}
        ref={fileField}
        disabled={disabled}
        onChange={handleOnChange}
        type="file"
        accept=".jpg,.png"
      />
      <ImageContainer
        disabled={disabled}
        onClick={handleOnClick}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleOnDrop}
      >
        {!value && (
          <PlaceholderContainer>
            {disabled ? (
              <AddImage />
            ) : (
              <>
                <AddImage />
                <DragNDropText>Click here or drag to upload</DragNDropText>
                <FormatHint>Accepted formats: jpg, png (Max 9mb)</FormatHint>
              </>
            )}
          </PlaceholderContainer>
        )}
        {value && <ImagePreview src={value} alt="" />}
        {!disabled && (
          <LoaderOverlay isLoading={isLoading || isDragActive}>
            {isLoading ? (
              <>
                <Spinner />
                <LoaderText>Uploading in progress</LoaderText>
              </>
            ) : null}
            {!isLoading && isDragActive ? (
              <LoaderText>Drop to upload image</LoaderText>
            ) : null}
            {!isLoading && !isDragActive ? (
              <LoaderText>Click to upload image</LoaderText>
            ) : null}
          </LoaderOverlay>
        )}
      </ImageContainer>
      <ErrorMessage component={Error} name={field.name} />
    </Container>
  );
};
